import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money, employeePayrollStats } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/PrefsContext";
import {
  PageHeader, PrimaryButton, EmptyState, Modal, Field, Input, Textarea, SelectBox, Button, Badge,
} from "@/components/ui-kit";
import { Plus, Briefcase, Pencil, Trash2, Mail, Phone, Eye, Users, Star } from "lucide-react";

const EMPTY = {
  name: "", phone: "", email: "", role: "Cleaner", crew: "", availability: "",
  performance: "", pay_rate: 0, pay_type: "Hourly", status: "Active", hire_date: "", notes: "",
};

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("") || "?";

const payUnit = (t) => (t === "Hourly" ? "hr" : t === "Per Job" ? "job" : "yr");

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const { t } = useI18n();
  const navigate = useNavigate();

  const load = () => {
    api.get("/employees").then(({ data }) => setEmployees(data));
    api.get("/payroll").then(({ data }) => setPayroll(data));
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (e) => { setForm({ ...EMPTY, ...e }); setEditing(e.id); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error(t("Employee name is required"));
    const payload = { ...form, pay_rate: Number(form.pay_rate) };
    try {
      if (editing) await api.put(`/employees/${editing}`, payload);
      else await api.post("/employees", payload);
      toast.success(editing ? t("Employee updated") : t("Employee added"));
      setOpen(false); load();
    } catch { toast.error("Could not save employee"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    await api.delete(`/employees/${id}`); toast.success(t("Employee deleted")); load();
  };

  const balanceFor = (empId) =>
    employeePayrollStats(payroll.filter((p) => p.employee_id === empId)).balance;

  return (
    <div data-testid="employees-page">
      <PageHeader title="Employees" subtitle="Manage your cleaning crew, pay rates and balances."
        action={<PrimaryButton onClick={openNew} data-testid="add-employee-button"><Plus size={16} /> {t("Add Employee")}</PrimaryButton>} />

      {employees.length === 0 ? (
        <EmptyState icon={Briefcase} title="No employees yet" subtitle="Add your crew members so you can assign them to scheduled jobs."
          action={<PrimaryButton onClick={openNew}><Plus size={16} /> {t("Add Employee")}</PrimaryButton>} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="employees-grid">
          {employees.map((e) => {
            const balance = balanceFor(e.id);
            return (
              <div key={e.id} data-testid={`employee-card-${e.id}`}
                className="bg-white rounded-2xl border border-[#f0ebf7] shadow-[0_4px_18px_-10px_rgba(90,24,154,0.2)] p-5 flex flex-col gap-4 hover:shadow-[0_10px_30px_-12px_rgba(90,24,154,0.35)] hover:-translate-y-0.5 transition-all duration-300">
                {/* Header */}
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center font-display text-lg font-bold text-[#D4AF37] shrink-0"
                    style={{ background: "linear-gradient(135deg, #7B2CBF 0%, #5A189A 100%)" }}>
                    {initials(e.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-lg font-bold text-[#1a1523] truncate">{e.name}</div>
                    <div className="text-sm text-[#6b647a]">{t(e.role) || e.role}</div>
                    {e.crew && (
                      <div className="flex items-center gap-1 text-xs text-[#9b8bb5] mt-0.5">
                        <Users size={12} /> {e.crew}
                      </div>
                    )}
                  </div>
                  {e.performance && (
                    <Badge color="#D4AF37"><Star size={11} className="mr-1" />{e.performance}</Badge>
                  )}
                </div>

                {/* Rate + Balance */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#faf8fd] rounded-xl px-3 py-2.5">
                    <div className="text-[11px] uppercase tracking-wider text-[#9b8bb5] font-semibold">{t("Hourly Rate")}</div>
                    <div className="font-bold text-[#5A189A] mt-0.5">{money(e.pay_rate)}<span className="text-xs text-[#9b8bb5] font-medium">/{payUnit(e.pay_type)}</span></div>
                  </div>
                  <div className="bg-[#faf8fd] rounded-xl px-3 py-2.5">
                    <div className="text-[11px] uppercase tracking-wider text-[#9b8bb5] font-semibold">{t("Balance")}</div>
                    <div className={`font-bold mt-0.5 ${balance > 0.001 ? "text-[#D4AF37]" : "text-green-600"}`} data-testid={`employee-balance-${e.id}`}>{money(balance)}</div>
                  </div>
                </div>

                {/* Contact */}
                <div className="text-sm text-[#52525B] space-y-1">
                  {e.phone && <div className="flex items-center gap-2"><Phone size={13} className="text-[#9b8bb5]" /> {e.phone}</div>}
                  {e.email && <div className="flex items-center gap-2 truncate"><Mail size={13} className="text-[#9b8bb5]" /> {e.email}</div>}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-1 mt-auto">
                  <button onClick={() => navigate(`/employees/${e.id}`)} data-testid={`view-employee-${e.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-white transition-all hover:opacity-95"
                    style={{ background: "linear-gradient(135deg, #7B2CBF 0%, #5A189A 100%)" }}>
                    <Eye size={15} /> {t("View Details")}
                  </button>
                  {e.email && (
                    <a href={`mailto:${e.email}`} title={t("Email")} data-testid={`email-employee-${e.id}`}
                      className="w-9 h-9 rounded-xl bg-[#F1EBFA] text-[#5A189A] flex items-center justify-center hover:bg-[#e7ddf5] transition">
                      <Mail size={16} />
                    </a>
                  )}
                  <button onClick={() => openEdit(e)} title={t("Edit")} data-testid={`edit-employee-${e.id}`}
                    className="w-9 h-9 rounded-xl bg-[#F1EBFA] text-[#5A189A] flex items-center justify-center hover:bg-[#e7ddf5] transition">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => remove(e.id)} title={t("Delete")} data-testid={`delete-employee-${e.id}`}
                    className="w-9 h-9 rounded-xl bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Employee" : "Add Employee"} size="lg"
        footer={<><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><PrimaryButton onClick={save} data-testid="save-employee-button">Save Employee</PrimaryButton></>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="employee-name-input" /></Field>
          <Field label="Role"><SelectBox value={form.role} onChange={(e) => set("role", e.target.value)} options={["Cleaner", "Team Lead", "Supervisor", "Manager"]} /></Field>
          <Field label="Crew"><Input value={form.crew} onChange={(e) => set("crew", e.target.value)} placeholder="e.g. Crew A" data-testid="employee-crew-input" /></Field>
          <Field label="Performance"><Input value={form.performance} onChange={(e) => set("performance", e.target.value)} placeholder="e.g. Excellent" data-testid="employee-performance-input" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
          <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="Hourly Rate ($)"><Input type="number" value={form.pay_rate} onChange={(e) => set("pay_rate", e.target.value)} /></Field>
          <Field label="Pay Type"><SelectBox value={form.pay_type} onChange={(e) => set("pay_type", e.target.value)} options={["Hourly", "Per Job", "Salary"]} /></Field>
          <Field label="Availability"><Input value={form.availability} onChange={(e) => set("availability", e.target.value)} placeholder="e.g. Mon–Fri, 8am–5pm" data-testid="employee-availability-input" /></Field>
          <Field label="Hire Date"><Input type="date" value={form.hire_date} onChange={(e) => set("hire_date", e.target.value)} /></Field>
          <Field label="Status"><SelectBox value={form.status} onChange={(e) => set("status", e.target.value)} options={["Active", "Inactive"]} /></Field>
          <Field label="Notes" className="sm:col-span-2"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
      </Modal>
    </div>
  );
}
